import {getCompanyOperationsManagerId, getCompanyOperationsManagerOptions} from "shared/utils/companyOperationsManager";
import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Company as CompanyType} from "shared/types/Company";
import {User as UserType} from "shared/types/User";
import {CompanyEvent} from "shared/types/CompanyEvent";
import {CompanyStatus} from "shared/types/CompanyStatuses";
import {
    getActiveCompanies,
    getAllMeetingsByIds,
    getCompanyStatuses,
    getCurrentUserCompanies,
    getEvents,
    getUsersByBusinessId,
    getUsersByCompany,
    refreshSummary,
    type CurrentUserAccessProfile,
} from "shared/api/client";
import {Meeting} from "shared/types/Meeting";
import {getStoredClientWorkspace, saveClientWorkspaceState} from "shared/utils/clientWorkspaceStorage";
import {useLocation} from "react-router-dom";
import {
    decodeAuthTokenClaims,
    normalizeUserAccountRole,
    shouldUseUserAccountsExperience,
    USER_ACCOUNT_ROLES,
    type UserAccountRole,
} from "shared/utils/userAccountSession";
import {useUserAccountsAccessProfile} from "shared/hooks/useUserAccountsAccessProfile";

const POLL_INTERVAL_MS = 20_000;
const IDLE_MS = 30 * 60 * 1000;
export const NO_OPERATOR_USER_FILTER_ID = "__NO_OPERATOR_USER__";
export const NOTACTIVE_COMPANY_STATUS = "NOTACTIVE";

const ACTIVITY_EVENTS = [
    "mousemove",
    "mousedown",
    "keydown",
    "scroll",
    "touchstart",
    "pointermove",
    "wheel",
] as const;

type Business = {
    id: string;
    business_name: string;
    [key: string]: any;
};
export const EVENT_PILLS = [
    {key: "DONE", label: "טופל", kind: "status"},
    {key: "WAITING_FOR_HANDLE", label: "לטיפול", kind: "status"},
    {key: "DEFERRED", label: "לטיפול בהמשך", kind: "status"},
    {key: "UNRELEVANT", label: "לא רלוונטי", kind: "status"},
] as const;

export type EventsPillKey = typeof EVENT_PILLS[number]["key"];

const DEFAULT_EVENTS_PILL_KEY: EventsPillKey = "WAITING_FOR_HANDLE";

const isEventsPillKey = (value: string | null): value is EventsPillKey => {
    return EVENT_PILLS.some((pill) => pill.key === value);
};

const getInitialEventsPillKey = (): EventsPillKey => {
    const storedPillKey = getStoredClientWorkspace().eventsPillKey;
    return isEventsPillKey(storedPillKey) ? storedPillKey : DEFAULT_EVENTS_PILL_KEY;
};

interface ChatContextProps {
    loading: boolean;

    companyId: string | null;
    setCompanyId: React.Dispatch<React.SetStateAction<string | null>>;

    company: CompanyType | null;
    setCompany: React.Dispatch<React.SetStateAction<CompanyType | null>>;

    searchQuery: string;
    setSearchQuery: React.Dispatch<React.SetStateAction<string>>;

    users: UserType[];
    setUsers: React.Dispatch<React.SetStateAction<UserType[]>>;
    fetchUsersByCompany: (companyId: string) => Promise<void>;

    companyStatuses: CompanyStatus[];
    fetchCompanyStatuses: () => Promise<CompanyStatus[]>;

    companies: CompanyType[];
    setCompanies: React.Dispatch<React.SetStateAction<CompanyType[]>>;
    fetchCompanies: () => Promise<void>;

    fetchGetAllMeetingsByIds: () => Promise<Meeting[]>;

    selectedStatuses: string[];
    setSelectedStatuses: React.Dispatch<React.SetStateAction<string[]>>;
    reloadEvents: () => Promise<void>;

    filteredCompanies: CompanyType[];

    operatorUsers: UserType[];
    setOperatorUsers: React.Dispatch<React.SetStateAction<UserType[]>>;
    fetchOperatorUsers: () => Promise<void>;
    isOperatorUsersLoading: boolean;

    selectedBusinessIds: string[];
    setSelectedBusinessIds: React.Dispatch<React.SetStateAction<string[]>>;
    selectedProductNames: string[];
    setSelectedProductNames: React.Dispatch<React.SetStateAction<string[]>>;
    selectedOperatorUserIds: string[];
    setSelectedOperatorUserIds: React.Dispatch<React.SetStateAction<string[]>>;

    companySelectionStatus: string;
    setCompanySelectionStatus: React.Dispatch<React.SetStateAction<string>>;

    events: CompanyEvent[];
    setEvents: React.Dispatch<React.SetStateAction<CompanyEvent[]>>;

    eventsAssignee: string;
    setEventsAssignee: React.Dispatch<React.SetStateAction<string>>;

    eventsType: string;
    setEventsType: React.Dispatch<React.SetStateAction<string>>;

    business: string;
    setBusiness: React.Dispatch<React.SetStateAction<string>>;

    usesUserAccounts: boolean;
    userAccountRole: UserAccountRole | null;
    hasAllBusinessAccess: boolean;
    isHkWorkspace: boolean;
    userAccessProfile: CurrentUserAccessProfile | null;
    userAccessProfileSignature: string | null;
    isUserAccessProfileLoading: boolean;
    userAccessProfileError: Error | null;
    refreshUserAccessProfile: () => Promise<CurrentUserAccessProfile | null>;

    sessionId: string;
    setSessionId: React.Dispatch<React.SetStateAction<string>>;

    openNewMeeting: boolean;
    setOpenNewMeeting: React.Dispatch<React.SetStateAction<boolean>>;

    isEditMeeting: Meeting | undefined;
    setIsEditMeeting: React.Dispatch<React.SetStateAction<Meeting | undefined>>;

    allCompaniesMeetings: Meeting[] | undefined;

    isLoadingEvents: boolean;
    setIsLoadingEvents: React.Dispatch<React.SetStateAction<boolean>>;

    setSelectedBusinessNames: React.Dispatch<React.SetStateAction<string[]>>;
    selectedBusinessNames: string[];

    setBusinesses: React.Dispatch<React.SetStateAction<Business[]>>;
    businesses: Business[];

    userId: string;
    setUserId: React.Dispatch<React.SetStateAction<string>>;

    useHarigaSort: boolean;
    handleToggleChange: () => void;

    jobTitleIdAdmin: boolean;

    eventPills: readonly { key: string; label: string; kind: string }[];
    eventsPillKey: EventsPillKey;
    setEventsPillKey: React.Dispatch<React.SetStateAction<EventsPillKey>>;

    filteredEvents: CompanyEvent[];
    clearEventFilters: () => void;


    openPrepMeetingPanel: boolean;
    setOpenPrepMeetingPanel: React.Dispatch<React.SetStateAction<boolean>>;

    selectedPrepMeeting: Meeting | null;
    setSelectedPrepMeeting: React.Dispatch<React.SetStateAction<Meeting | null>>;
}

const ChatContext = createContext<ChatContextProps | undefined>(undefined);

export const ChatProvider: React.FC<{
    children: React.ReactNode;
    deferBackgroundLoads?: boolean;
}> = ({children, deferBackgroundLoads = false}) => {
    const location = useLocation();
    const isAdminSettingsRoute = location.pathname === "/admin-settings" || location.pathname.startsWith("/admin-settings/");
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [loading] = useState(false);
    const [company, setCompany] = useState<CompanyType | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [users, setUsers] = useState<UserType[]>([]);
    const [companyStatuses, setCompanyStatuses] = useState<CompanyStatus[]>([]);
    const [companies, setCompanies] = useState<CompanyType[]>([]);
    const [allCompaniesMeetings, setAllCompaniesMeetings] = useState<Meeting[] | undefined>();
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>(["הכל"]);
    const [operatorUsers, setOperatorUsers] = useState<UserType[]>([]);
    const [selectedBusinessIds, setSelectedBusinessIds] = useState<string[]>([]);
    const [selectedProductNames, setSelectedProductNames] = useState<string[]>([]);
    const [selectedOperatorUserIds, setSelectedOperatorUserIds] = useState<string[]>([]);
    const [companySelectionStatus, setCompanySelectionStatus] = useState<string>("1");
    const [events, setEvents] = useState<CompanyEvent[]>([]);
    const [business, setBusiness] = useState<string>("");
    const [sessionId, setSessionId] = useState<string>("");
    const [isLoadingEvents, setIsLoadingEvents] = useState(true);
    const [isOperatorUsersLoading, setIsOperatorUsersLoading] = useState(!deferBackgroundLoads);
    const [openNewMeeting, setOpenNewMeeting] = useState(false);
    const [isEditMeeting, setIsEditMeeting] = useState<Meeting | undefined>();
    const [eventsPillKey, setEventsPillKey] = useState<EventsPillKey>(getInitialEventsPillKey);
    const [selectedBusinessNames, setSelectedBusinessNames] = useState<string[]>([]);
    const [businesses, setBusinesses] = useState<Business[]>([]);
    const [userId, setUserId] = useState<string>("");
    const [eventsAssignee, setEventsAssignee] = useState<string>("");
    const [eventsType, setEventsType] = useState<string>("all");
    const isRefreshingRef = useRef(false);
    const companiesRequestGenerationRef = useRef(0);
    const eventsRequestGenerationRef = useRef(0);
    const inFlightEventsRef = useRef<Map<string, number>>(new Map());
    const lastFetchedEventsKeyRef = useRef<string | null>(null);
    const SORT_KEY = "companiesSortUseHariga";
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pollingRunningRef = useRef(false);
    const stoppedForIdleRef = useRef(false);
    const operatorUsersRequestGenerationRef = useRef(0);

    const [selectedPrepMeeting, setSelectedPrepMeeting] = useState<Meeting | null>(null);
    const [openPrepMeetingPanel, setOpenPrepMeetingPanel] = useState(false);
    const [authToken, setAuthToken] = useState<string | null>(
        () => localStorage.getItem("authToken")
    );
    useEffect(() => {
        const syncAuthToken = () => {
            const nextToken = localStorage.getItem("authToken");
            setAuthToken(currentToken => {
                if (currentToken === nextToken) return currentToken;

                const currentClaims = decodeAuthTokenClaims(currentToken);
                const nextClaims = decodeAuthTokenClaims(nextToken);
                return shouldUseUserAccountsExperience(currentClaims) ||
                    shouldUseUserAccountsExperience(nextClaims)
                    ? nextToken
                    : currentToken;
            });
        };
        const onStorage = (event: StorageEvent) => {
            if (event.key === "authToken") syncAuthToken();
        };
        const onVisible = () => {
            if (document.visibilityState === "visible") syncAuthToken();
        };

        window.addEventListener("storage", onStorage);
        window.addEventListener("focus", syncAuthToken);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("focus", syncAuthToken);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, []);
    const sessionClaims = useMemo(() => decodeAuthTokenClaims(authToken), [authToken]);
    const usesUserAccounts = shouldUseUserAccountsExperience(sessionClaims);
    const {
        profile: userAccessProfile,
        isLoading: isUserAccessProfileLoading,
        error: userAccessProfileError,
        refresh: refreshUserAccessProfile,
    } = useUserAccountsAccessProfile(
        usesUserAccounts,
        sessionClaims?.auth_user_id,
        sessionClaims?.email
    );
    const normalizedProfileRole = normalizeUserAccountRole(userAccessProfile?.access.role);
    const userAccountRole = Object.values(USER_ACCOUNT_ROLES).includes(
        normalizedProfileRole as UserAccountRole
    )
        ? normalizedProfileRole as UserAccountRole
        : null;
    const userAccessProfileSignature = useMemo(() => {
        if (!userAccessProfile) return null;

        return JSON.stringify({
            authUserAccountId: userAccessProfile.user.authUserAccountId,
            email: userAccessProfile.user.email,
            role: userAccessProfile.access.role,
            businessId: userAccessProfile.user.businessId,
            companySelectedId: userAccessProfile.user.companySelectedId,
            visibleScreens: [...userAccessProfile.user.visibleScreens].sort(),
            companyIds: [...userAccessProfile.access.companyIds].sort((a, b) => a - b),
            businessIds: [...userAccessProfile.access.businessIds].sort((a, b) => a - b),
            activeOnly: userAccessProfile.access.activeOnly,
            capabilities: Object.entries(userAccessProfile.capabilities)
                .sort(([left], [right]) => left.localeCompare(right)),
        });
    }, [userAccessProfile]);
    const authorizationScopeKey = usesUserAccounts
        ? `user-accounts:${String(sessionClaims?.auth_user_id ?? "")}:${String(sessionClaims?.email ?? "").trim().toLowerCase()}:${userAccessProfileSignature ?? "unavailable"}`
        : "legacy";
    const authorizationScopeKeyRef = useRef(authorizationScopeKey);
    authorizationScopeKeyRef.current = authorizationScopeKey;
    const hasUserAccessProfile = userAccessProfile !== null;
    const canAccessGlobalAdminTools =
        userAccessProfile?.capabilities.canAccessGlobalAdminTools === true;
    const userHasAllBusinessAccess = usesUserAccounts &&
        userAccessProfile?.capabilities.canViewAllBusinesses === true;
    const isHkWorkspace = usesUserAccounts ? userHasAllBusinessAccess : business === "1";

    const [useHarigaSort, setUseHarigaSort] = useState<boolean>(() => {
        if (typeof window === "undefined") return false;
        const stored = window.localStorage.getItem(SORT_KEY);
        return stored === "1";
    });

    const handleToggleChange = () => {
        setUseHarigaSort(prev => {
            const next = !prev;
            if (typeof window !== "undefined") {
                window.localStorage.setItem(SORT_KEY, next ? "1" : "0");
            }
            return next;
        });
    };

    const fetchOperatorUsers = useCallback(async () => {
        const requestGeneration = ++operatorUsersRequestGenerationRef.current;
        const requestedAuthorizationScopeKey = authorizationScopeKey;

        if (usesUserAccounts && !hasUserAccessProfile) {
            setOperatorUsers([]);
            setIsOperatorUsersLoading(false);
            return;
        }

        setIsOperatorUsersLoading(true);
        try {
            const users = await getUsersByBusinessId();
            if (
                operatorUsersRequestGenerationRef.current !== requestGeneration ||
                authorizationScopeKeyRef.current !== requestedAuthorizationScopeKey
            ) return;
            setOperatorUsers(users);
        } catch (error) {
            console.error("Failed to fetch operator users:", error);
        } finally {
            if (
                operatorUsersRequestGenerationRef.current === requestGeneration &&
                authorizationScopeKeyRef.current === requestedAuthorizationScopeKey
            ) {
                setIsOperatorUsersLoading(false);
            }
        }
    }, [authorizationScopeKey, hasUserAccessProfile, usesUserAccounts]);

    useEffect(() => {
        if (deferBackgroundLoads) {
            operatorUsersRequestGenerationRef.current += 1;
            setOperatorUsers([]);
            setIsOperatorUsersLoading(false);
            return;
        }
        void fetchOperatorUsers();
    }, [deferBackgroundLoads, fetchOperatorUsers]);

    const operatorUsersById = useMemo(() => {
        const m = new Map<string, UserType>();
        for (const u of operatorUsers) m.set(String(u.id), u);
        return m;
    }, [operatorUsers]);

    const jobTitleIdAdmin = usesUserAccounts
        ? canAccessGlobalAdminTools
        : String(operatorUsersById.get(String(userId))?.job_title_id ?? "").toUpperCase() === "ADMIN";

    useEffect(() => {
        if (usesUserAccounts) {
            if (!hasUserAccessProfile) {
                setEventsAssignee("");
                return;
            }

            // User_Accounts visibility is company-based; do not reapply the legacy
            // assignee restriction after the server authorized the company.
            setEventsAssignee("all");
            return;
        }

        if (!userId) return;

        if (operatorUsers.length === 0) return;

        if (jobTitleIdAdmin) {
            setEventsAssignee("all");
        } else {
            setEventsAssignee(String(userId));
        }
    }, [hasUserAccessProfile, jobTitleIdAdmin, operatorUsers, userId, usesUserAccounts]);

    useEffect(() => {
        if (usesUserAccounts) {
            setBusiness(
                userAccessProfile?.user.businessId == null
                    ? ""
                    : String(userAccessProfile.user.businessId)
            );
            setUserId(
                userAccessProfile?.user.legacyUserId == null
                    ? ""
                    : String(userAccessProfile.user.legacyUserId)
            );
        } else if (sessionClaims) {
            setBusiness(String(sessionClaims.business_id ?? ""));
        }
    }, [
        sessionClaims,
        userAccessProfile?.user.businessId,
        userAccessProfile?.user.legacyUserId,
        usesUserAccounts,
    ]);

    useEffect(() => {
        if (!usesUserAccounts && !business) {
            return;
        }

        setEventsPillKey((current) => {
            if (!isHkWorkspace && current === "UNRELEVANT") {
                return DEFAULT_EVENTS_PILL_KEY;
            }

            return current;
        });
    }, [business, isHkWorkspace, usesUserAccounts]);

    useEffect(() => {
        if (companySelectionStatus !== "2" || !companyId) {
            return;
        }

        saveClientWorkspaceState({
            companyId,
            eventsPillKey,
        });
    }, [companyId, companySelectionStatus, eventsPillKey]);

    const buildEventsCountByCompanyId = (events: CompanyEvent[]) => {
        return events.reduce<Record<string, number>>((acc, ev) => {
            const key = String(ev.company_id);
            if (ev.event_status_name === "לטיפול") {
                acc[key] = (acc[key] || 0) + 1;
            }
            return acc;
        }, {});
    };

    useEffect(() => {
        if (companies.length === 0) return;

        const eventsCountByCompanyId = buildEventsCountByCompanyId(events);

        setCompanies(prev =>
            prev.map(company => ({
                ...company,
                eventsCount: eventsCountByCompanyId[String(company.id)] || 0,
            }))
        );
    }, [events, companies.length]);

    const filteredCompanies = useMemo(() => {
        const getCompanyProductNames = (company: any): string[] => {
            const raw = company?.products;
            if (!Array.isArray(raw)) return [];
            return raw
                .map((p: any) => (typeof p === "string" ? p : ""))
                .filter(Boolean);
        };

        let filtered = companies.filter((company) => company.is_active !== false);

        if (selectedBusinessIds.length > 0) {
            filtered = filtered.filter((company) =>
                company.business_id && selectedBusinessIds.map(Number).includes(Number(company.business_id))
            );
        }

        // Product filter (business 1 only)
        if (isHkWorkspace && selectedProductNames.length > 0) {
            filtered = filtered.filter((company: any) => {
                const companyProducts = getCompanyProductNames(company);
                return companyProducts.some((productName) => selectedProductNames.includes(productName));
            });
        }

        if (selectedOperatorUserIds.length > 0) {
            const knownOperatorIds = new Set(
                getCompanyOperationsManagerOptions(companies).map(manager => manager.id)
            );
            const selectedKnownOperatorIds = selectedOperatorUserIds.filter((id) => knownOperatorIds.has(id));
            const includesNoOperator = selectedOperatorUserIds.includes(NO_OPERATOR_USER_FILTER_ID);
            const hasEffectiveOperatorSelection = selectedKnownOperatorIds.length > 0 || includesNoOperator;
            const allOperatorOptionsSelected =
                includesNoOperator &&
                selectedKnownOperatorIds.length === knownOperatorIds.size &&
                selectedKnownOperatorIds.every((id) => knownOperatorIds.has(id));

            if (hasEffectiveOperatorSelection && !allOperatorOptionsSelected) {
                filtered = filtered.filter((company) => {
                    const companyOperatorId = getCompanyOperationsManagerId(company);
                    const hasKnownOperator = Boolean(companyOperatorId) && knownOperatorIds.has(companyOperatorId);

                    return (
                        (hasKnownOperator && selectedKnownOperatorIds.includes(companyOperatorId)) ||
                        (includesNoOperator && !hasKnownOperator)
                    );
                });
            }
        }

        const getCompanyBusinessStatusKey = (company: CompanyType) =>
            String(company.status?.status ?? "");

        const getCompanyFilterStatusKey = (company: CompanyType) =>
            String(
                isHkWorkspace
                    ? company.status?.status ?? ""
                    : company.consultant_ui_status?.ui_status_key ?? ""
            );

        const isNotActiveCompanyStatus = (company: CompanyType) =>
            getCompanyBusinessStatusKey(company) === NOTACTIVE_COMPANY_STATUS;

        if (selectedStatuses.includes("הכל")) {
            return filtered.filter((company) => !isNotActiveCompanyStatus(company));
        }

        if (selectedStatuses.includes(NOTACTIVE_COMPANY_STATUS)) {
            return filtered.filter((company) => isNotActiveCompanyStatus(company));
        }

        return filtered.filter((company) =>
            selectedStatuses.includes(getCompanyFilterStatusKey(company))
        );
    }, [companies, selectedStatuses, selectedBusinessIds, selectedProductNames, selectedOperatorUserIds, isHkWorkspace]);

    // Which companies are currently visible in the sidebar (after filters).
    // Events list should be limited to these companies.
    const visibleCompanyIds = useMemo(() => {
        return new Set(filteredCompanies.map((c) => String(c.id)));
    }, [filteredCompanies]);

    const fetchGetAllMeetingsByIds = useCallback(async (): Promise<Meeting[]> => {
        try {
            const companyIds = companies.map(c => Number(c.id)).filter(Number.isFinite);
            if (companyIds.length === 0) {
                setAllCompaniesMeetings([]);
                return [];
            }
            const meetings = await getAllMeetingsByIds(companyIds);
            setAllCompaniesMeetings(meetings);
            return meetings;
        } catch (err) {
            console.error("Error fetching meetings by ids:", err);
            setAllCompaniesMeetings([]);
            return [];
        }
    }, [companies]);


    const fetchCompanies = useCallback(async () => {
        const requestGeneration = ++companiesRequestGenerationRef.current;
        const requestScopeKey = authorizationScopeKey;
        const canCommit = () =>
            companiesRequestGenerationRef.current === requestGeneration &&
            authorizationScopeKeyRef.current === requestScopeKey;

        if (usesUserAccounts && !hasUserAccessProfile) {
            if (canCommit()) {
                setCompanies([]);
                setAllCompaniesMeetings([]);
            }
            return;
        }

        try {
            const data = usesUserAccounts
                ? await getCurrentUserCompanies()
                : await getActiveCompanies();
            if (!canCommit()) return;
            setCompanies(data);

            const companyIds = data
                .map(c => Number(c.id))
                .filter(Number.isFinite);

            if (companyIds.length > 0) {
                const meetings = await getAllMeetingsByIds(companyIds);
                if (canCommit()) setAllCompaniesMeetings(meetings);
            } else {
                if (canCommit()) setAllCompaniesMeetings([]);
            }
        } catch (error) {
            console.error("Error fetching active companies:", error);
            if (canCommit()) {
                setCompanies([]);
                setAllCompaniesMeetings([]);
            }
        }
    }, [authorizationScopeKey, hasUserAccessProfile, usesUserAccounts]);

    useEffect(() => {
        if (deferBackgroundLoads) return;
        fetchCompanies();
    }, [deferBackgroundLoads, fetchCompanies]);


    const fetchEvents = useCallback(async (opts?: { force?: boolean; skipLoading?: boolean }): Promise<void> => {
        if (usesUserAccounts && !hasUserAccessProfile) {
            eventsRequestGenerationRef.current += 1;
            setEvents([]);
            setIsLoadingEvents(false);
            lastFetchedEventsKeyRef.current = null;
            return;
        }

        const force = !!opts?.force;
        const skipLoading = !!opts?.skipLoading;
        const requestScopeKey = authorizationScopeKey;
        const requestKey = `${requestScopeKey}:${eventsPillKey}`;

        if (inFlightEventsRef.current.get(requestKey) === eventsRequestGenerationRef.current) {
            return;
        }

        if (!force && lastFetchedEventsKeyRef.current === requestKey) {
            return;
        }

        const requestGeneration = ++eventsRequestGenerationRef.current;
        inFlightEventsRef.current.set(requestKey, requestGeneration);
        lastFetchedEventsKeyRef.current = requestKey;
        const canCommit = () =>
            eventsRequestGenerationRef.current === requestGeneration &&
            authorizationScopeKeyRef.current === requestScopeKey;

        try {
            if (!skipLoading) {
                setIsLoadingEvents(true);
            }
            const evs = await getEvents(eventsPillKey);
            if (!canCommit()) return;
            setEvents(evs);

            if (!skipLoading) {
                setTimeout(() => {
                    if (canCommit()) setIsLoadingEvents(false);
                }, evs.length > 500 ? 3000 : 0);
            }

        } catch (error) {
            console.error("Error fetching events:", error);
            if (canCommit()) {
                setEvents([]);
                if (!skipLoading) {
                    setIsLoadingEvents(false);
                }
                lastFetchedEventsKeyRef.current = null;
            }
        } finally {
            if (inFlightEventsRef.current.get(requestKey) === requestGeneration) {
                inFlightEventsRef.current.delete(requestKey);
            }
        }
    }, [authorizationScopeKey, eventsPillKey, hasUserAccessProfile, usesUserAccounts]);

    const reloadEvents = useCallback(async (): Promise<void> => {
        await fetchEvents({force: true});
    }, [fetchEvents]);


    const refreshData = useCallback(async () => {
        if (isAdminSettingsRoute || deferBackgroundLoads) return;
        if (usesUserAccounts && !hasUserAccessProfile) return;
        if (isRefreshingRef.current) return;
        isRefreshingRef.current = true;

        try {
            const summary = await refreshSummary();
            if (summary?.hasCompaniesUpdates) {
                await fetchCompanies();
            }
            if (summary?.hasEventsUpdates) {
                await fetchEvents({ force: true, skipLoading: true });
            }
        } catch (error) {
            console.error("Error in refreshData:", error);
        } finally {
            isRefreshingRef.current = false;
        }
    }, [deferBackgroundLoads, fetchCompanies, fetchEvents, hasUserAccessProfile, isAdminSettingsRoute, usesUserAccounts]);

    const stopPolling = useCallback(() => {
        pollingRunningRef.current = false;
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    const startPolling = useCallback(() => {
        if (isAdminSettingsRoute || deferBackgroundLoads) return;
        if (pollingRunningRef.current) return;

        pollingRunningRef.current = true;
        stoppedForIdleRef.current = false;

        const tick = async () => {
            if (!pollingRunningRef.current) return;

            try {
                await refreshData();
            } finally {
                if (!pollingRunningRef.current) return;
                pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
            }
        };

        void tick();
    }, [deferBackgroundLoads, isAdminSettingsRoute, refreshData]);

    const armIdleTimer = useCallback(() => {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

        idleTimerRef.current = setTimeout(() => {
            stoppedForIdleRef.current = true;
            stopPolling();
        }, IDLE_MS);
    }, [stopPolling]);

    const onActivity = useCallback(() => {
        if (typeof document !== "undefined" && document.hidden) return;

        armIdleTimer();

        if (!pollingRunningRef.current && stoppedForIdleRef.current) {
            startPolling();
        }
    }, [armIdleTimer, startPolling]);

    useEffect(() => {
        if (deferBackgroundLoads || isAdminSettingsRoute || (usesUserAccounts && !hasUserAccessProfile)) {
            stopPolling();
            if (idleTimerRef.current) {
                clearTimeout(idleTimerRef.current);
                idleTimerRef.current = null;
            }
            return;
        }

        onActivity();
        startPolling();

        const opts: AddEventListenerOptions = {passive: true};

        ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, opts));

        const onVisibility = () => {
            if (document.hidden) {
                stopPolling();
            } else {
                onActivity();
                if (!pollingRunningRef.current) startPolling();
            }
        };

        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("focus", onActivity);
        return () => {
            ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("focus", onActivity);

            stopPolling();

            if (idleTimerRef.current) {
                clearTimeout(idleTimerRef.current);
                idleTimerRef.current = null;
            }
        };
    }, [deferBackgroundLoads, hasUserAccessProfile, isAdminSettingsRoute, onActivity, startPolling, stopPolling, usesUserAccounts]);


    useEffect(() => {
        if (deferBackgroundLoads) {
            setCompanyStatuses([]);
            return;
        }
        const getStatuses = async () => {
            try {
                const data = await fetchCompanyStatuses();
                setCompanyStatuses(data);
            } catch (error) {
                console.error("Failed to fetch company statuses:", error);
            }
        };
        getStatuses();
    }, [deferBackgroundLoads]);


    const fetchUsersByCompany = async (companyId: string) => {
        if (usesUserAccounts && !hasUserAccessProfile) {
            setUsers([]);
            return;
        }

        try {
            setUsers([]); // Reset first
            const fetchedUsers = await getUsersByCompany(companyId);
            setUsers(fetchedUsers);
        } catch (error) {
            console.error("❌ Error fetching users:", error);
        }
    };

    const fetchCompanyStatuses = async (): Promise<CompanyStatus[]> => {
        try {
            const statuses = await getCompanyStatuses();
            setCompanyStatuses(statuses);
            return statuses;
        } catch (error) {
            console.error("Error fetching company statuses:", error);
            return [];
        }
    };

    // const deleteMessage = async (companyId: string, messageId: string) => {
    //     try {
    //         const response = await deleteMessageApi(companyId, messageId);
    //         if (response.deleted) {
    //             setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== messageId));
    //             console.log(`✅ Message deleted: ${messageId}`);
    //         } else {
    //             console.error(`❌ Failed to delete message: ${messageId}`);
    //         }
    //     } catch (error) {
    //         console.error("❌ Error deleting message:", error);
    //     }
    // };

    const clearEventFilters = () => {
        if (isHkWorkspace) {
            setEventsPillKey("WAITING_FOR_HANDLE");
        } else {
            setEventsPillKey("DONE");
        }
    };

    const eventsForVisibleCompanies = useMemo(() => {
        return events
            .filter(ev => visibleCompanyIds.has(String(ev.company_id)))
            .map(ev => {
                const u = ev.assignee_user_id ? operatorUsersById.get(String(ev.assignee_user_id)) : undefined;
                return {
                    ...ev,
                    assignee_user_name: u ? `${u.first_name} ${u.last_name}` : "",
                };
            });
    }, [events, visibleCompanyIds, operatorUsersById]);

    const filteredEvents = useMemo(() => {
        let list = eventsForVisibleCompanies;
        console.log("filteredEvents");

        if (eventsAssignee && eventsAssignee !== "all") {
            const assigneeNum = Number(eventsAssignee);
            list = list.filter(ev => Number(ev.assignee_user_id) === assigneeNum);
        }

        if (eventsType && eventsType !== "all") {
            list = list.filter(ev => ev.event_type === eventsType);
        }
        return list;
    }, [eventsForVisibleCompanies, eventsAssignee, eventsType]);

    const eventPills = useMemo(
        () =>
            isHkWorkspace
                ? EVENT_PILLS
                : EVENT_PILLS.filter((p) => p.key !== "UNRELEVANT"),
        [isHkWorkspace]
    );

    useEffect(() => {
        if (deferBackgroundLoads) {
            lastFetchedEventsKeyRef.current = null;
            setEvents([]);
            setIsLoadingEvents(false);
            return;
        }
        lastFetchedEventsKeyRef.current = null;
        void fetchEvents();
    }, [deferBackgroundLoads, fetchEvents]);

    return (
        <ChatContext.Provider
            value={{
                loading,
                companyId,
                setCompanyId,
                users,
                setUsers,
                fetchUsersByCompany,
                company,
                setCompany,
                searchQuery,
                setSearchQuery,
                companyStatuses,
                fetchCompanyStatuses,
                companies,
                setCompanies,
                fetchCompanies,
                fetchGetAllMeetingsByIds,
                selectedStatuses,
                setSelectedStatuses,
                filteredCompanies,
                operatorUsers,
                setOperatorUsers,
                fetchOperatorUsers,
                isOperatorUsersLoading,
                selectedBusinessIds,
                setSelectedBusinessIds,
                selectedProductNames,
                setSelectedProductNames,
                selectedOperatorUserIds,
                setSelectedOperatorUserIds,
                companySelectionStatus,
                setCompanySelectionStatus,
                events,
                setEvents,
                eventsAssignee,
                setEventsAssignee,
                eventsType,
                setEventsType,
                business,
                setBusiness,
                usesUserAccounts,
                userAccountRole,
                hasAllBusinessAccess: userHasAllBusinessAccess,
                isHkWorkspace,
                userAccessProfile,
                userAccessProfileSignature,
                isUserAccessProfileLoading,
                userAccessProfileError,
                refreshUserAccessProfile,
                sessionId,
                setSessionId,
                allCompaniesMeetings,
                isLoadingEvents,
                setIsLoadingEvents,
                setSelectedBusinessNames,
                selectedBusinessNames,
                setBusinesses,
                businesses,
                userId,
                setUserId,
                useHarigaSort,
                handleToggleChange,
                jobTitleIdAdmin,
                openNewMeeting,
                setOpenNewMeeting,
                isEditMeeting,
                setIsEditMeeting,
                eventPills,
                eventsPillKey,
                setEventsPillKey,
                filteredEvents,
                clearEventFilters,
                reloadEvents,
                openPrepMeetingPanel,
                setOpenPrepMeetingPanel,
                selectedPrepMeeting,
                setSelectedPrepMeeting
            }}
        >
            {children}
        </ChatContext.Provider>
    );
};

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error("useChat must be used within a ChatProvider");
    }
    return context;
};
